import type { BaseCollection } from '@/models/BaseCollection';
import { ServerTotalUpdated } from '@/models/BaseDataProxy';
import type { BaseModel } from '@/models/BaseModel';
import type { QueryParams } from '@kp/js-model';
import type { QueryOptions } from '@kp/js-model/dist/SharedTypes';
import { readonly, ref, toRef, toValue, watch, type DeepReadonly, type Ref, type UnwrapNestedRefs } from 'vue';

export type UseSortOptions = {
    autoLoad: boolean;
};

/**
 * Type for the 'filters' property of the Backend api.
 * filters takes an array of ApiFilter types.
 */
export type ApiFilter = {
    id?: string;
    property: string;
    value: any;
    operator?: string;
};

export type SortOption = {
    property: string;
    direction?: 'asc' | 'desc';
};

/**
 * Creates reactive Sort properties for a given collection: It tracks the given SortOption array
 * and applies the sort to the collection if autoLoad is true, transforming the SortOption to
 * the API sort format.
 *
 * The function returns 2 reactive variables:
 * - ktgTableSort: The sort property array with the data formatted for KtgTable
 * - apiSort: The sort property array with the data formatted for the backend API
 *
 * @param collection The collection to (re-)load
 * @param initialSort The initial sort array, e.g. [{dataProperty: 'name', direction: 'ascending'}]
 * @param options Additional options:
 *   - autoLoad: boolean: If true (default), the collection will automatically be reloaded if the sort changes
 * @returns An object containing 2 reactive variables: ktgTableSort and apiSort: ktgTableSort is to be used with
 *   KtgTable, and apiSort is to be used with the backend API
 */
export function useSortProp<T extends BaseModel>(
    collection: BaseCollection<T>,
    initialSort?: SortOption[] | Ref<SortOption[]> | null,
    options: UseSortOptions = { autoLoad: true },
): {
    ktgTableSort: Ref<SortOption[]>;
    apiSort: Ref<{ property: string; direction: string }[]>;
} {
    const ktgTableSort = ref(initialSort || []);
    const apiSort: Ref<{ property: string; direction: string }[]> = ref([]);

    const applySort = (sort: SortOption[]) => {
        const sortProps = sort.map((s) => ({
            property: s.property,
            direction: s.direction === 'desc' ? 'desc' : 'asc',
        }));
        apiSort.value = sortProps;
        collection.setQueryParams({
            sort: sortProps,
        });
    };

    if (toValue(initialSort)?.length) {
        applySort(toValue(initialSort)!);
    }

    watch(
        ktgTableSort,
        (newVal) => {
            applySort(newVal);
            if (options?.autoLoad) {
                collection.query();
            }
        },
        { immediate: options?.autoLoad ?? false },
    );

    return {
        ktgTableSort,
        apiSort,
    };
}

/**
 * Creates reactive Paging properties for a given collection: it creates reactive
 * variables for pageSize and page and applies the paging to the collection if changed.
 * Also, it watches the serverTotal and calculates the available pages.
 *
 * @param collection The collection to page-watch
 * @param pageSize The initial page size, defaults to 250
 * @param page The initial page number, defaults to 1 (1 = first page)
 * @returns An object with the following reactive properties:
 *   - pageSize: The current page size
 *   - page: The current page number
 *   - pages: The total number of pages, based on the returned serverTotal
 *   - serverTotal: The total number of records, as sent from the server
 */
export function usePaging<T extends BaseModel>(
    collection: BaseCollection<T>,
    pageSize: number | Ref<number> = 250,
    page: number | Ref<number> = 1,
) {
    const refPageSize = toRef(pageSize);
    const refPage = toRef(page);
    const refPages: Ref<number> = ref(0);
    const serverTotal = ref(0);

    collection.getDataProxy().addObserver((e) => {
        if (e instanceof ServerTotalUpdated) {
            serverTotal.value = e.serverTotal;
            refPages.value = refPageSize.value > 0 ? Math.ceil(serverTotal.value / refPageSize.value) : 0;
        }
    });

    const applyPaging = (pageSize: number, page: number) => {
        const limit = pageSize;
        const offset = (page - 1) * pageSize;
        collection.setQueryParams({
            limit,
            start: offset,
        });
    };

    applyPaging(toValue(pageSize), toValue(page));

    const reload = async () => {
        applyPaging(refPageSize.value, refPage.value);
        await collection.query();
    };

    watch(refPage, () => reload());
    watch(refPageSize, () => reload());

    return {
        pageSize: refPageSize,
        page: refPage,
        pages: refPages,
        serverTotal,
    };
}

export type UseCollectionOptions = {
    tableSort?: SortOption[] | Ref<SortOption[]>;
    collectionsFilter?: ApiFilter[] | Ref<ApiFilter[]>;
    pageSize?: number | Ref<number>;
    page?: number | Ref<number>;
    autoload?: boolean;
};

export type UseCollectionData<C extends BaseCollection<T>, T extends BaseModel> = {
    collection: C;
    models: Ref<UnwrapNestedRefs<T[]>>;
    tableSort: Ref<SortOption[]>;
    apiSort: DeepReadonly<Ref<{ property: string; direction: string }[]>>;
    filters: Ref<ApiFilter[]>;
    page: Ref<number>;
    pages: Ref<number>;
    pageSize: Ref<number>;
    serverTotal: Ref<number>;
    query: (queryParams?: QueryParams | null, opts?: QueryOptions | null) => Promise<C>;
    reload: () => Promise<C>;
};
/**
 * This composable creates a reactive connection between a Collection and its params,
 * sort and paging properties. It is meant to be used with KtgTable, to create a remote-backed table,
 * which reacts on sorting, filtering, paging.
 *
 * @param collection A BaseCollection to use
 * @param options
 * @returns An object with the following reactive props:
 *   - collection: the given collection, unchanged
 *   - models: a reactive reference to the collection's models
 *   - tableSort: The reactive sort property array with the data formatted for KtgTable
 *   - apiSort: The reactive sort property array with the data formatted for the backend API
 *   - filters: The reactive filter property array
 *   - page: The actual page number
 *   - pages: The total number of pages, based on the returned serverTotal
 *   - pageSize: The actual page size
 *   - serverTotal: The total number of records, as sent from the server
 *   - query: The collection's query method, can be used to manually re-load the collection if needed
 */
export function useCollection<C extends BaseCollection<T>, T extends BaseModel>(
    collection: C,
    options: UseCollectionOptions = {},
): UseCollectionData<C, T> {
    const filters = ref(options.collectionsFilter ?? []);
    collection.setQueryParam('filter', filters.value);

    // The useSortProp helper will return 2 reactive props:
    // - ktgTableSort: The sort property array with the data formatted for KtgTable
    // - apiSort: The sort property array with the data formatted for the backend API
    // If the table sort params change, the data will be reloaded (collection.query() is executed).
    const { ktgTableSort: tableSort, apiSort } = useSortProp(collection, options.tableSort ?? null, {
        autoLoad: false,
    });
    const { pageSize, page, pages, serverTotal } = usePaging(collection, options.pageSize ?? 250, options.page ?? 1);

    const queryCollection = async (): Promise<C> => {
        await collection.query();
        return collection;
    };

    collection.getDataProxy().addObserver((e) => {
        if (e instanceof ServerTotalUpdated) {
            serverTotal.value = e.serverTotal;
            pages.value = pageSize.value > 0 ? Math.ceil(serverTotal.value / pageSize.value) : 0;
        }
    });

    watch(filters, async (newVal) => {
        collection.setQueryParam('filter', newVal);
        await queryCollection();
    });

    watch(tableSort, async () => {
        await queryCollection();
    });

    if (options.autoload && !collection.loading.value) {
        collection.query();
    }

    return {
        collection,
        models: ref(collection.getModels()),
        tableSort,
        apiSort: readonly(apiSort),
        filters,
        page,
        pages: readonly(pages),
        pageSize,
        serverTotal: readonly(serverTotal),
        query: collection.query.bind(collection),
        reload: queryCollection,
    };
}
